// ✅ bankAccountSlice.js (with .env support)
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL;

// Add Bank Account
export const addBankAccount = createAsyncThunk(
  'bankAccount/addBankAccount',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/bank-account`, formData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Add failed');
    }
  }
);

// Fetch Bank Accounts
export const fetchBankAccounts = createAsyncThunk(
  'bankAccount/fetchBankAccounts',
  async ({ page = 1, superAdminId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/bank-accounts?page=${page}&superAdminId=${superAdminId}`
      );

      return {
        data: response.data.accounts,
        totalPages: response.data.totalPages
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Update Bank Account
export const updateBankAccount = createAsyncThunk(
  'bankAccount/updateBankAccount',
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/bank-account/${id}`, updatedData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Update failed');
    }
  }
);

// Delete Bank Account
export const deleteBankAccount = createAsyncThunk(
  'bankAccount/deleteBankAccount',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/bank-account/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Delete failed');
    }
  }
);

// Slice
const bankAccountSlice = createSlice({
  name: 'bankAccount',
  initialState: {
    loading: false,
    success: false,
    error: null,
    accounts: [],
    totalPages: 1,
    currentPage: 1
  },
  reducers: {
    resetStatus: state => {
      state.loading = false;
      state.success = false;
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // Add
      .addCase(addBankAccount.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addBankAccount.fulfilled, state => {
        state.loading = false;
        state.success = true;
      })
      .addCase(addBankAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch
      .addCase(fetchBankAccounts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload.data;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchBankAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateBankAccount.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBankAccount.fulfilled, state => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateBankAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteBankAccount.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBankAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.accounts = state.accounts.filter(acc => acc.id !== action.payload);
      })
      .addCase(deleteBankAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetStatus } = bankAccountSlice.actions;
export default bankAccountSlice.reducer;
